import React, { useEffect, useState } from "react";
import "./table-view-dropdown.scss";
import { Dropdown, Button, Flex, Modal, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTable,
  faChevronDown,
  faRightFromBracket,
  faUsers,
  faSave,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import CreateAndEditTableView from "./create-and-edit";
import { ITableViews, ITableViewItem, ITableViewsEditParams } from "../../interfaces/table-view";
import { TableViewServices } from "../../services/table-view";
import { useMessageApi } from "../../context/MessageContext";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";

interface TableViewDropdownProps {
  tableName: string;
  dataTableView: ITableViews;
  onTrigger: () => void;
}

const TableViewDropdown: React.FC<TableViewDropdownProps> = ({ tableName, dataTableView, onTrigger }) => {
  const messageApi = useMessageApi();
  const { myViews = [], sharedViews = [], currentView, systemView = [] } = dataTableView || {};
  const [openModal, setOpenModal] = useState(false);
  const [isModalDelete, setIsModalDelete] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [selectItemView, setSelectItemView] = useState<ITableViewItem>();
  const isViewSystem = currentView?.reports && currentView?.reports!.length > 0;

  const hasEditorSystemReport = useHasPermission([PERMISSIONS.SYSTEM_REPORT_ADMIN_EDITOR]);

  useEffect(() => {
    setSelectItemView({
      tableName: "",
      name: "",
      id: "",
      shareCount: 0,
      sharedWith: [],
      ...currentView,
    });
  }, [currentView]);

  const handleDone = () => {
    onTrigger();
    setOpenDropdown(false);
  };

  const isCheckCurrentView = (item: ITableViewItem) => {
    return item.id === selectItemView?.id;
  };

  const handleSetCurrentView = async (item: ITableViewItem, isTrigger = true) => {
    if (isCheckCurrentView(item)) return;
    try {
      setSelectItemView(item);
      const res: any = await TableViewServices.setCurrentView(item.id, tableName);
      if (res?.success) {
        // messageApi.success(res?.message || "");
        if (isTrigger) {
          handleDone();
        }
      }
    } catch (err) {}
  };

  const handleSaveTableView = async (item: ITableViewItem) => {
    if (!isCheckCurrentView(item)) return;
    try {
      const body = {
        name: item.name,
        columns: currentView?.columns || [],
        filters: currentView?.filters,
        ...(isViewSystem && { reports: currentView?.reports }),
      };
      const res: any = await TableViewServices.updateTableViews(item.id, body);
      if (res?.success) {
        messageApi.success(res?.message || "");
        handleDone();
      }
    } catch (err: any) {
      messageApi.success(err?.message || "");
    }
  };

  const handleCreateAndEditTableView = async (formValue: ITableViewsEditParams) => {
    const body = {
      ...formValue,
      columns: currentView?.columns || [],
      filters: currentView?.filters,
      tableName: tableName,
      ...(isViewSystem && { reports: currentView?.reports }),
    };
    if (isNameDuplicate(formValue.name)) {
      messageApi.error("This view already existed, please choose another name");
      return false;
    }
    try {
      const res: any = isEdit
        ? await TableViewServices.updateTableViews(selectItemView?.id!, body)
        : await TableViewServices.createTableViews(body);
      if (res?.success) {
        messageApi.success(res?.message || "");
        if (!isEdit && res?.data?.id) {
          setSelectItemView(res?.data);
          handleSetCurrentView(res?.data, false);
        }
      }
      return true;
    } catch (err: any) {
      messageApi.error(err?.message || "");
      return false;
    }
  };

  const handleExitTableView = async (viewId: string) => {
    try {
      const res: any = await TableViewServices.exitTableViews(viewId);
      if (res?.success) {
        setSelectItemView(undefined);
        handleDone();
        messageApi.success(res?.message || "");
      }
    } catch (err) {}
  };

  const isNameDuplicate = (name: string): boolean => {
    const normalizedName = name.trim().toLowerCase();
    return myViews.some(
      (item, index, arr) =>
        item.name.trim().toLowerCase() === normalizedName &&
        arr.findIndex((el) => el.name.trim().toLowerCase() === normalizedName) !== index
    );
  };

  const handleShowModal = () => {
    setOpenDropdown(false);
    setOpenModal(true);
  };

  const showModalDelete = (item: ITableViewItem) => {
    setSelectItemView(item);
    setOpenDropdown(false);
    setIsModalDelete(true);
  };

  const handleCancelDelete = () => {
    setIsModalDelete(false);
    setOpenDropdown(true);
  };

  const handleDelete = async () => {
    if (!selectItemView?.id) return;
    setIsLoadingDelete(true);
    try {
      const res: any = await TableViewServices.deleteTableViews(selectItemView?.id);
      if (res?.success) {
        messageApi.success(res?.message || "");
        setIsModalDelete(false);
        handleDone();
      }
    } catch (error: any) {
      messageApi.error(error?.message);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const renderName = (item: ITableViewItem) => (
    <Tooltip title={item.name.length > 14 ? item.name : ""}>
      <span className="name" onClick={() => handleSetCurrentView(item)}>
        {item.name}
      </span>
    </Tooltip>
  );

  const renderMyViews = () => (
    <div className="box">
      <Flex justify="space-between" align="center">
        <h4 className="title">My Views</h4>
        <Button
          type="primary"
          className="btn-new"
          size="small"
          onClick={() => {
            setIsEdit(false);
            handleShowModal();
          }}
        >
          Create new
        </Button>
      </Flex>
      {myViews.length > 0 && (
        <div className="list">
          {myViews.map((item) => (
            <Flex
              justify="space-between"
              align="center"
              gap={16}
              className={`item ${isCheckCurrentView(item) ? "active" : ""}`}
              key={item.id}
            >
              {renderName(item)}

              <Flex justify="end" align="center" gap={8}>
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: "16px" }} />
                <span>({item.shareCount})</span>
              </Flex>

              <Flex className="box-action" gap={8}>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveTableView(item);
                  }}
                  type="link"
                  disabled={!isCheckCurrentView(item)}
                >
                  <FontAwesomeIcon icon={faSave} />
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEdit(true);
                    handleShowModal();
                  }}
                  type="link"
                  disabled={!isCheckCurrentView(item)}
                >
                  <FontAwesomeIcon icon={faPen} />
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    showModalDelete(item);
                  }}
                  type="link"
                  disabled={!isCheckCurrentView(item)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExitTableView(item.id);
                  }}
                  type="link"
                  disabled={!isCheckCurrentView(item)}
                >
                  <FontAwesomeIcon icon={faRightFromBracket} />
                </Button>
              </Flex>
            </Flex>
          ))}
        </div>
      )}
    </div>
  );

  const renderSharedViews = () => (
    <>
      {sharedViews.length > 0 && (
        <div className="box">
          <h4 className="title">Shared Views</h4>
          <div className="list">
            {sharedViews.map((item) => (
              <Flex
                justify="space-between"
                align="center"
                gap={16}
                className={`item ${isCheckCurrentView(item) ? "active" : ""}`}
                key={item.id}
              >
                {renderName(item)}

                <Flex justify="end" align="center" gap={8} className="box-action">
                  <span>{`${item?.firstName} ${item?.lastName}`}</span>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExitTableView(item.id);
                    }}
                    type="link"
                    disabled={!isCheckCurrentView(item)}
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} />
                  </Button>
                </Flex>
              </Flex>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderSystemViews = () => (
    <>
      {systemView.length > 0 && (
        <div className="box">
          <h4 className="title">System Views</h4>
          <div className="list">
            {systemView.map((item) => (
              <Flex
                justify="space-between"
                align="center"
                gap={16}
                className={`item ${isCheckCurrentView(item) ? "active" : ""}`}
                key={item.id}
              >
                {renderName(item)}

                <Flex className="box-action" gap={8} style={{ maxWidth: "95px", textAlign: "right" }}>
                  {hasEditorSystemReport && (
                    <>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveTableView(item);
                        }}
                        type="link"
                        disabled={!isCheckCurrentView(item)}
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEdit(true);
                          handleShowModal();
                        }}
                        type="link"
                        disabled={!isCheckCurrentView(item)}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          showModalDelete(item);
                        }}
                        type="link"
                        disabled={!isCheckCurrentView(item)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </>
                  )}

                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExitTableView(item.id);
                    }}
                    type="link"
                    disabled={!isCheckCurrentView(item)}
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} />
                  </Button>
                </Flex>
              </Flex>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderContent = () => (
    <div className="wrap-table-view">
      {renderMyViews()}
      {renderSharedViews()}
      {renderSystemViews()}
    </div>
  );

  const renderModalDelete = () => (
    <Modal
      title="Confirm delete"
      closable={{ "aria-label": "Custom Close Button" }}
      open={isModalDelete}
      onOk={handleDelete}
      onCancel={handleCancelDelete}
      okText="Delete"
      confirmLoading={isLoadingDelete}
    >
      <p style={{ fontSize: "20px" }}>
        Are you sure you want to delete table view{" "}
        <span className="text-primary font-italic">{selectItemView?.name || currentView?.name}</span>? <br />
        This action cannot be undone.
      </p>
    </Modal>
  );

  return (
    <>
      <Dropdown
        popupRender={(menu) => renderContent()}
        trigger={["click"]}
        placement="bottomRight"
        open={openDropdown}
        onOpenChange={(isOpen) => setOpenDropdown(isOpen)}
      >
        <Button
          type="primary"
          onClick={(e) => e.preventDefault()}
          style={{ background: "var(--text-blue-500)", minWidth: "180px" }}
        >
          <FontAwesomeIcon icon={faTable} style={{ fontSize: "16px" }} />
          {selectItemView?.name || "Table View"}
          <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "16px" }} />
        </Button>
      </Dropdown>
      {openModal && (
        <Modal
          open={openModal}
          onCancel={() => setOpenModal(false)}
          footer={null}
          width={500}
          title={isEdit ? "Edit View" : "Create View"}
        >
          <CreateAndEditTableView
            onClose={(okTriggered?: boolean) => {
              setOpenModal(false);
              if (okTriggered) {
                handleDone();
              } else {
                setOpenDropdown(true);
              }
            }}
            onSave={handleCreateAndEditTableView}
            detail={isEdit ? selectItemView : undefined}
            isViewSystem={isViewSystem && hasEditorSystemReport}
          />
        </Modal>
      )}
      {renderModalDelete()}
    </>
  );
};

export default TableViewDropdown;
